import { CartItemModel } from '@src/app/types';
import EditButton from '../button/EditButton';
import QuantitySelector from '../quantity-selector/QuantitySelector';
import { useEffect, useState } from 'react';
import { OrderInfoContext, OrderInfo } from '@src/app/order-info-context';
import { OrderFlowContext } from '@src/app/order-flow-context';
import DatePicker from '@components/date-picker/DatePicker';
import TimePicker from '@components/time-picker/TimePicker';
import _ from 'lodash';
import IconWithTooltip from '../icon-with-tooltip/IconWithTooltip';

interface Props extends CartItemModel {
  canEdit?: boolean;
  editText?: string;
  applyText?: string;
  hidePostcode?: boolean;
  onRemoveFromCart: () => any;
  onSetQuantity: (quantity: number) => any;
  handleTimeSubmit?: (item: CartItemModel, data: { schedule: number[]; session: string; firstCollectionDate: string }) => any;
  extraClassname?: string;
  removeFromCartBtnText?: string;
  removeFromCartConfirmBtnText?: string;
  removeFromCartDialogText?: string;
  deliveryTimeLabelText?: string;
}

const CartItem = ({
  removeFromCartBtnText,
  removeFromCartConfirmBtnText,
  removeFromCartDialogText,
  applyText,
  extraClassname,
  canEdit,
  editText,
  hidePostcode,
  onRemoveFromCart,
  onSetQuantity,
  handleTimeSubmit,
  quantityRemaining,
  unitNote,
  tooltip,
  tooltipLabel,
  ...item
}: Props) => {
  const schedule = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  let selectedSchedule: string[] = [];
  if (item.schedule) {
    selectedSchedule = item.schedule.map((i) => schedule[i]);
  }

  if (item.session) {
    selectedSchedule.push(item.session.toUpperCase());
  }
  const scheduleText = selectedSchedule.join(', ');

  const formattedDate = item.firstCollection?.replace(/([0-9]{4})-([0-9]{1,2})-([0-9]{1,2})/gi, '$3/$2/$1');

  const [weeklySchedule, setWeeklySchedule] = useState<number[] | undefined>(item.schedule);
  const [session, setSession] = useState<string | undefined>(item.session);
  const [firstCollection, setFirstCollection] = useState<string | undefined>(item.firstCollection);
  const [quantity, setQuantity] = useState(item.quantity);
  const [serviceId, setServiceId] = useState(item.serviceId);
  const [groupId, setGroupId] = useState<number | undefined>(item.groupId);
  const [postcode, setPostcode] = useState(item.postcode);
  const [productCode, setProductCode] = useState<string | undefined>(item.productCode);
  const [variantId, setVariantId] = useState<number | undefined>(item.variantId);
  const [currentScheduleText, setCurrentScheduleText] = useState<string>(scheduleText);

  const blankFn = () => {};

  const orderInfoCtxValue: OrderInfo = {
    quantity,
    setQuantity,
    serviceId,
    setServiceId,
    firstCollection,
    setFirstCollection,
    groupId,
    setGroupId,
    postcode,
    setPostcode,
    productCode,
    setProductCode,
    schedule: weeklySchedule,
    setSchedule: setWeeklySchedule,
    session,
    setSession,
    variantId,
    setVariantId,
  };

  const [editWeeklyScheduleInProgress, setEditWeeklyScheduleInProgress] = useState(false);
  const [editWeeklyScheduleBtnApply, setEditWeeklyScheduleBtnApply] = useState(false);
  const [editFirstCollectionInProgress, setEditFirstCollectionInProgress] = useState(false);

  const handleEditWeeklyScheduleBtnClick = () => {
    setEditWeeklyScheduleInProgress(true);
    if (editFirstCollectionInProgress) setEditFirstCollectionInProgress(false);
  };

  const handleEditFirstCollectionBtnClick = () => {
    setEditFirstCollectionInProgress(true);
    if (editWeeklyScheduleInProgress) {
      setEditWeeklyScheduleInProgress(false);
    }
  };

  const isFirstCollectionValid = () => {
    const firstCollectionDate = new Date(`${firstCollection}T00:00:01`);
    const weekDay = firstCollectionDate.getDay() === 0 ? 6 : firstCollectionDate.getDay() - 1;
    if (weeklySchedule!.includes(weekDay)) return true;
    return false;
  };

  const disableFirstCollectionSubmitBtn = firstCollection === item.firstCollection || !isFirstCollectionValid();

  const handleWeeklyScheduleApplyBtnClick = () => {
    setEditWeeklyScheduleInProgress(false);
    if (isFirstCollectionValid()) {
      if (handleTimeSubmit)
        handleTimeSubmit(item, { schedule: [...weeklySchedule!.sort((a, b) => a - b)], session: session!, firstCollectionDate: firstCollection! });
    } else {
      setEditFirstCollectionInProgress(true);
    }
  };

  const handleFirstCollectionApplyBtnClick = () => {
    setEditFirstCollectionInProgress(false);
    if (handleTimeSubmit)
      handleTimeSubmit(item, { schedule: [...weeklySchedule!.sort((a, b) => a - b)], session: session!, firstCollectionDate: firstCollection! });
  };

  useEffect(() => {
    if (!weeklySchedule) return;
    const sortedSchedule = [...weeklySchedule!].sort((a, b) => a - b);
    const currentSchedule = sortedSchedule.map((i) => schedule[i]);
    if (session) {
      currentSchedule.push(session.toUpperCase());
    }
    const newScheduleText = currentSchedule.join(', ');
    setCurrentScheduleText(newScheduleText);

    const disableWeeklyScheduleSubmitBtn =
      item.session === session &&
      _.isEqual(
        item.schedule && [...item.schedule].sort((a, b) => a - b),
        [...weeklySchedule!].sort((a, b) => a - b)
      );
    setEditWeeklyScheduleBtnApply(disableWeeklyScheduleSubmitBtn);
  }, [weeklySchedule, session]);

  // check should show quantity remaining
  const showQuantityRemaining =
    item.bidAllocationProduct && parseFloat(item.priceIncludedTax) === 0 && typeof quantityRemaining === 'number' && quantityRemaining < 10000;

  // set max quantity
  let maxQuantity = item.maxQuantity;

  if (typeof maxQuantity === 'number' && typeof quantityRemaining === 'number') {
    maxQuantity = Math.min(maxQuantity, quantityRemaining + quantity);
  }

  if (item.isUnavailable) {
    return (
      <div className={`d-flex align-items-center fm-o-cart-item fm-o-cart-item--unavailable ${extraClassname ?? ''}`}>
        <div className="d-flex align-items-center">
          <svg className="fm-o-cart-item__icon-when-unavailable">
            <use href={item.iconSrcWhenUnavailable} />
          </svg>
        </div>
        <div className="fm-o-cart-item__info">
          <h4 className="fm-o-cart-item__title">{item.name}</h4>
          <p className="fm-o-cart-item__reason">{item.unavailabilityReason}</p>
        </div>
      </div>
    );
  }

  return (
    <OrderInfoContext.Provider value={orderInfoCtxValue}>
      <OrderFlowContext.Provider value={{ handleBack: blankFn, handleClose: blankFn, handleContinue: blankFn }}>
        <div className={`fm-o-cart-item ${extraClassname ?? ''}`}>
          <div className="fm-o-cart-item__container">
            <div className="fm-o-cart-item__main">
              <div className="fm-o-cart-item__title-bar">
                <h4 className="fm-o-cart-item__title">{item.name}</h4>
                <div className="fm-o-cart-item__description">{item.description}</div>
              </div>
              <div className={`fm-o-cart-item__price ${item.promoPrice && 'fm-o-cart-item__price-has-promo'}`}>
                {item.price} {!!item.promoPrice && <span className="fm-o-cart-item__price-old">{item.promoPrice}</span>}
                {!!unitNote && <div className="fm-o-cart-item__unit-note" dangerouslySetInnerHTML={{ __html: unitNote }}></div>}
                {!!tooltipLabel && (
                  <div className="fm-o-cart-item__tooltip">
                    {tooltipLabel} {!!tooltip && <IconWithTooltip text="test" iconColor="#2d3047" />}
                  </div>
                )}
              </div>
            </div>
            <div className="fm-o-cart-item__quantity-selector">
              <QuantitySelector
                minQuantity={item.minQuantity}
                maxQuantity={maxQuantity}
                quantity={item.quantity}
                showRemove={true}
                onSetQuantity={onSetQuantity}
                onRemoveFromCart={onRemoveFromCart}
                extraClassname="fm-o-quantity-selector--cart-page"
                removeFromCartBtnText={removeFromCartBtnText}
                removeFromCartConfirmBtnText={removeFromCartConfirmBtnText}
                removeFromCartDialogText={removeFromCartDialogText}
                batchQuantity={item.batchQuantity ?? 1}
              />
            </div>

            {(item.postcode || item.schedule || item.firstCollection || editWeeklyScheduleInProgress || showQuantityRemaining) && (
              <div className="fm-o-cart-item__props">
                {showQuantityRemaining && (
                  <div className="fm-o-cart-item__prop fm-o-cart-item__qty-remaining">
                    <div className="fm-o-cart-item__prop-name">Allocation Remaining</div>
                    <div className="fm-o-cart-item__prop-value" translate="no">
                      {quantityRemaining}
                    </div>
                  </div>
                )}

                {item.postcode && !hidePostcode && (
                  <div className="fm-o-cart-item__prop">
                    <div className="fm-o-cart-item__prop-name">Postcode</div>
                    <div className="fm-o-cart-item__prop-value" translate="no">
                      {item.postcode}
                    </div>
                  </div>
                )}
                {item.variantId !== 0 && item.schedule && item.schedule.length > 0 && (
                  <div className="fm-o-cart-item__prop">
                    <div className="fm-o-cart-item__prop-name">Schedule</div>
                    {!editWeeklyScheduleInProgress && (
                      <div className="fm-o-cart-item__prop-value fm-o-cart-item__prop-schedule-value">{currentScheduleText}</div>
                    )}
                    {canEdit && !editWeeklyScheduleInProgress && (
                      <EditButton mainClass="fm-o-cart-item__edit-btn" text={editText} onClick={handleEditWeeklyScheduleBtnClick} />
                    )}
                  </div>
                )}
                {editWeeklyScheduleInProgress && (
                  <>
                    <TimePicker
                      data={{
                        title: '',
                        ctaText: '',
                        noteText: '',
                        chooseTimeText: '',
                        collectingFromPostcodeText: '',
                        noteTitle: '',
                        changePostcodeText: '',
                      }}
                      extraClassname="fm-o-time-picker--cart-page"
                      keepFirstCollection={true}
                    />

                    <button
                      className={`fm-a-button tfm fm-a-button--dark fm-o-cart-item__apply-btn`}
                      onClick={handleWeeklyScheduleApplyBtnClick}
                      disabled={editWeeklyScheduleBtnApply}
                    >
                      {applyText}
                    </button>
                  </>
                )}
                {item.firstCollection && (
                  <div className="fm-o-cart-item__prop fm-o-cart-item__prop--1st-collect">
                    <div className="fm-o-cart-item__prop-name">{item.deliveryTimeLabelText ?? 'First collection'} </div>
                    <div className="fm-o-cart-item__prop-value" translate="no">
                      {formattedDate}
                    </div>
                    {canEdit && <EditButton mainClass="fm-o-cart-item__edit-btn" text={editText} onClick={handleEditFirstCollectionBtnClick} />}
                  </div>
                )}
                {editFirstCollectionInProgress && (
                  <>
                    <DatePicker data={{ title: '', ctaText: '', constraint: item.timeConstraint }} extraClassname="fm-o-date-picker--cart-page" />
                    <button
                      className={`fm-a-button tfm fm-a-button--dark fm-o-cart-item__apply-btn`}
                      onClick={handleFirstCollectionApplyBtnClick}
                      disabled={disableFirstCollectionSubmitBtn}
                    >
                      {applyText}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </OrderFlowContext.Provider>
    </OrderInfoContext.Provider>
  );
};

export default CartItem;
